/** @format */

import { useState, useEffect, useMemo } from "react";
import {
  collection,
  getDocs,
  query,
  limit,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { toast } from "react-hot-toast";

const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Simple state for basic functionality
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  // Fetch users with basic functionality
  const fetchUsers = async () => {
    try {
      console.log("🔍 Fetching users...");
      setLoading(true);

      let q = collection(db, "users");

      // Apply basic filters
      if (roleFilter && roleFilter !== "all") {
        // For array roles, we need to use array-contains
        q = query(q, where("role", "array-contains", roleFilter));
      }

      if (statusFilter && statusFilter !== "all") {
        q = query(q, where("status", "==", statusFilter));
      }

      // Apply sorting
      q = query(q, orderBy(sortBy, sortOrder));

      // Apply limit to prevent excessive reads (increased for classroom modal)
      q = query(q, limit(500));

      const usersSnapshot = await getDocs(q);
      console.log(`✅ Found ${usersSnapshot.size} users`);
      console.log("useUsers - Role filter:", roleFilter);
      console.log("useUsers - Status filter:", statusFilter);

      const usersData = usersSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
        };
      });

      console.log("useUsers - Processed users data:", usersData.length);
      console.log("useUsers - Sample user:", usersData[0]);
      console.log("useUsers - All user IDs:", usersData.map(u => u.id));

      setUsers(usersData);
      setTotalCount(usersData.length);
      setError(null);
    } catch (err) {
      console.error("❌ Error fetching users:", err);
      setError(err.message);
      toast.error(`Error fetching users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Search users
  const searchUsers = async (term) => {
    setSearchTerm(term);
    if (!term || term.trim() === "") {
      await fetchUsers();
    } else {
      // Simple search - filter existing users
      const filtered = users.filter(
        (user) =>
          user.name?.toLowerCase().includes(term.toLowerCase()) ||
          user.email?.toLowerCase().includes(term.toLowerCase())
      );
      setUsers(filtered);
      setTotalCount(filtered.length);
    }
  };

  // Apply filters
  const applyFilters = async (filters) => {
    if (filters.role !== undefined) {
      setRoleFilter(filters.role);
    }
    if (filters.status !== undefined) {
      setStatusFilter(filters.status);
    }
    if (filters.sortBy !== undefined) {
      setSortBy(filters.sortBy);
    }
    if (filters.sortOrder !== undefined) {
      setSortOrder(filters.sortOrder);
    }
    await fetchUsers();
  };

  // Clear filters
  const clearFilters = async () => {
    setRoleFilter("all");
    setStatusFilter("all");
    setSearchTerm("");
    await fetchUsers();
  };

  // Basic CRUD operations
  const addUser = async (userData) => {
    try {
      const docData = {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, "users"), docData);
      console.log(`✅ User added with ID: ${docRef.id}`);

      // Add to local state
      const newUser = { id: docRef.id, ...docData };
      setUsers((prev) => [newUser, ...prev]);
      setTotalCount((prev) => prev + 1);

      toast.success("User added successfully");
      return { success: true, id: docRef.id };
    } catch (err) {
      console.error("❌ Error adding user:", err);
      toast.error(`Error adding user: ${err.message}`);
      return { success: false, error: err.message };
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      const docRef = doc(db, "users", userId);
      const updateData = {
        ...userData,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(docRef, updateData);
      console.log(`✅ User updated: ${userId}`);

      // Update local state
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, ...updateData } : user
        )
      );

      toast.success("User updated successfully");
      return { success: true };
    } catch (err) {
      console.error("❌ Error updating user:", err);
      toast.error(`Error updating user: ${err.message}`);
      return { success: false, error: err.message };
    }
  };

  const deleteUser = async (userId) => {
    try {
      const docRef = doc(db, "users", userId);
      await deleteDoc(docRef);
      console.log(`✅ User deleted: ${userId}`);

      // Remove from local state
      setUsers((prev) => prev.filter((user) => user.id !== userId));
      setTotalCount((prev) => prev - 1);

      toast.success("User deleted successfully");
      return { success: true };
    } catch (err) {
      console.error("❌ Error deleting user:", err);
      toast.error(`Error deleting user: ${err.message}`);
      return { success: false, error: err.message };
    }
  };

  // Bulk operations
  const bulkUpdateUsers = async (userIds, updates) => {
    try {
      const updatePromises = userIds.map((userId) => {
        const docRef = doc(db, "users", userId);
        return updateDoc(docRef, {
          ...updates,
          updatedAt: serverTimestamp(),
        });
      });

      await Promise.all(updatePromises);
      console.log(`✅ ${userIds.length} users updated`);

      // Update local state
      setUsers((prev) =>
        prev.map((user) =>
          userIds.includes(user.id)
            ? { ...user, ...updates, updatedAt: new Date() }
            : user
        )
      );

      toast.success(`${userIds.length} users updated successfully`);
      return { success: true };
    } catch (err) {
      console.error("❌ Error updating users:", err);
      toast.error(`Error updating users: ${err.message}`);
      return { success: false, error: err.message };
    }
  };

  const bulkDeleteUsers = async (userIds) => {
    try {
      const deletePromises = userIds.map((userId) => {
        const docRef = doc(db, "users", userId);
        return deleteDoc(docRef);
      });

      await Promise.all(deletePromises);
      console.log(`✅ ${userIds.length} users deleted`);

      // Remove from local state
      setUsers((prev) => prev.filter((user) => !userIds.includes(user.id)));
      setTotalCount((prev) => prev - userIds.length);

      toast.success(`${userIds.length} users deleted successfully`);
      return { success: true };
    } catch (err) {
      console.error("❌ Error deleting users:", err);
      toast.error(`Error deleting users: ${err.message}`);
      return { success: false, error: err.message };
    }
  };

  // Categorized users
  const { authors, students, admins } = useMemo(() => {
    const isAuthorRole = (user) => {
      console.log("Checking user for author role:", user.name, "Role:", user.role, "Type:", typeof user.role);
      
      if (!user.role) {
        console.log("User has no role:", user.name);
        return false;
      }
      
      const roles = Array.isArray(user.role) ? user.role : [user.role];
      console.log("Processed roles for", user.name, ":", roles);
      
      const isAuthor = roles.some(
        (role) => {
          const normalizedRole = role ? role.toString().toLowerCase().trim() : '';
          return normalizedRole === "instructor" ||
                 normalizedRole === "admin" ||
                 normalizedRole === "teacher" ||
                 normalizedRole === "author";
        }
      );
      
      console.log("Is author?", user.name, ":", isAuthor);
      
      return isAuthor;
    };

    const isStudentRole = (user) => {
      if (!user.role) return true;
      const roles = Array.isArray(user.role) ? user.role : [user.role];
      return (
        roles.some(
          (role) => {
            const normalizedRole = role ? role.toString().toLowerCase().trim() : '';
            return normalizedRole === "student" || normalizedRole === "user" || normalizedRole === "";
          }
        ) ||
        !roles.some((role) => {
          const normalizedRole = role ? role.toString().toLowerCase().trim() : '';
          return ["instructor", "admin", "teacher", "author"].includes(normalizedRole);
        })
      );
    };

    const isAdminRole = (user) => {
      if (!user.role) return false;
      const roles = Array.isArray(user.role) ? user.role : [user.role];
      return roles.some((role) => {
        const normalizedRole = role ? role.toString().toLowerCase().trim() : '';
        return normalizedRole === "admin";
      });
    };

    const authorsList = users.filter(isAuthorRole);
    const studentsList = users.filter(isStudentRole);
    const adminsList = users.filter(isAdminRole);
    
    console.log("useUsers - Total users:", users.length);
    console.log("useUsers - Authors found:", authorsList.length);
    console.log("useUsers - Students found:", studentsList.length);
    console.log("useUsers - Admins found:", adminsList.length);
    
    // Log sample user data to understand structure
    if (users.length > 0) {
      console.log("Sample user data:", users[0]);
      console.log("All user roles:", users.map(u => ({ name: u.name, role: u.role, type: typeof u.role })));
    }
    
    return {
      authors: authorsList,
      students: studentsList,
      admins: adminsList,
    };
  }, [users]);

  // Pagination info (simplified)
  const paginationInfo = {
    currentPage: 1,
    totalPages: 1,
    pageSize: users.length,
    totalCount: users.length,
    startItem: 1,
    endItem: users.length,
    hasNextPage: false,
    hasPrevPage: false,
    isNavigating: false,
  };

  // Load users on mount
  useEffect(() => {
    console.log("useUsers - Starting to fetch users...");
    fetchUsers();
  }, []);

  // Refresh when filters change
  useEffect(() => {
    if (roleFilter !== "all" || statusFilter !== "all") {
      fetchUsers();
    }
  }, [roleFilter, statusFilter]);

  return {
    // State
    users,
    loading,
    error,
    totalCount,

    // Pagination state and info (simplified)
    paginationInfo,
    currentPage: 1,
    pageSize: users.length,
    hasNextPage: false,
    hasPrevPage: false,
    isNavigating: false,

    // Search and filter state
    searchTerm,
    roleFilter,
    statusFilter,
    sortBy,
    sortOrder,

    // Actions
    addUser,
    updateUser,
    deleteUser,
    bulkUpdateUsers,
    bulkDeleteUsers,
    searchUsers,
    applyFilters,
    clearFilters,
    refreshUsers: fetchUsers,

    // Pagination actions (simplified)
    nextPage: () => {},
    prevPage: () => {},
    goToPage: () => {},
    changePageSize: () => {},

    // Utilities
    getUserById: (id) => users.find((u) => u.id === id),
    getUsersByIds: (ids) => users.filter((u) => ids.includes(u.id)),

    // Categorized data
    authors,
    students,
    admins,

    // Raw data for backward compatibility
    allUsers: users,
  };
};

export default useUsers;
